import { MaterialsApi } from "@socotec.io/materials-sdk";
import { materialsConfig, fetchAll } from "@/setup/openApiConfig";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import omit from "lodash/omit";
import mapKeys from "lodash/mapKeys";
import camelCase from "lodash/camelCase";

dayjs.extend(utc);

const materialsApi = new MaterialsApi(materialsConfig);

// G.R 10/09/26 le SDK sérialise une date en `value.toISOString().substring(0, 10)` :
// un vrai Date plante sur la chaîne vide du formulaire et recule d'un jour à l'est de Greenwich
const toApiDate = (value) => ({
  toISOString: () => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
});

const toMaterialRequest = (form) => ({
  ...form,
  expirationDate: toApiDate(form.expirationDate),
  calibrationDate: toApiDate(form.calibrationDate),
});

const DATE_FIELDS = ["expirationDate", "calibrationDate"];

const fromMaterial = (material) => {
  const normalized = { ...material };
  for (const field of DATE_FIELDS) {
    normalized[field] = material[field]
      ? dayjs.utc(material[field]).format("YYYY-MM-DD")
      : null;
  }
  if (normalized.metrologicalParameters) {
    normalized.metrologicalParameters = mapKeys(
      normalized.metrologicalParameters,
      (value, key) => camelCase(key)
    );
  }
  return normalized;
};

export const materialRestClient = {
  listAll: async (filters = {}) => {
    const response = await fetchAll(
      materialsApi,
      "list",
      { page_size: 500, ...filters },
      Infinity
    );
    return { ...response, results: response.results.map(fromMaterial) };
  },

  retrieve: async (uuid) => fromMaterial(await materialsApi.retrieve({ uuid })),

  create: async (form) =>
    fromMaterial(
      await materialsApi.create({
        materialRequest: toMaterialRequest({
          ...form,
          category: { name: form.category },
        }),
      })
    ),

  update: async (form, uuid) => {
    const resourceId = uuid || form.uuid;
    if (!resourceId) {
      throw new Error("UUID is required for update operation");
    }
    // INFO - B.L - Cannot updates material category, it does would break metrological parameters
    return fromMaterial(
      await materialsApi.update({
        uuid: resourceId,
        materialRequest: toMaterialRequest(omit(form, ["category"])),
      })
    );
  },

  partialUpdate: async (form, uuid) => {
    const resourceId = uuid || form.uuid;
    if (!resourceId) {
      throw new Error("UUID is required for partial update operation");
    }
    return fromMaterial(
      await materialsApi.partialUpdate({
        uuid: resourceId,
        patchedMaterialRequest: toMaterialRequest(omit(form, ["category"])),
      })
    );
  },

  destroy: async (uuid) => {
    if (!uuid) {
      throw new Error("UUID is required for delete operation");
    }
    return await materialsApi.destroy({ uuid });
  },
};
